import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { FilePlus02, Trash02 } from "@untitledui/icons";
import { Button, ButtonUtility, Checkbox, Table } from "@tricura/atlas-ui";
import { EditableCellProvider, EditableDateCell, EditableTextCell } from "../../../../components/editable-cell";
import { FormGroup } from "../../../../components/form-group";
import { TableEmptyState } from "../../../../components/table-empty-state";
import { type UwInformationFormData, emptyAdditionalInsured } from "../schema";

// Sticky-column classes — split HEAD vs CELL so the table-header gray
// (bg-secondary) is preserved on TH while body cells stay white.
// Mobile (`<md`): drop the sticky pinning so the table scrolls as one
// block — the frozen actions column blanket a narrow viewport (OR-1280).
// Desktop (`md+`) unchanged. Matches Staffing Ratios.
const STICKY_HEAD = "md:sticky md:right-0 md:bg-secondary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]";
const STICKY_CELL = "md:sticky md:right-0 md:bg-primary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]";

export function AdditionalInsuredSection() {
    const { control } = useFormContext<UwInformationFormData>();
    const { fields, prepend, remove } = useFieldArray({ control, name: "additionalInsureds" });

    return (
        <FormGroup
            title="Additional Insured"
            flushBody
            action={
                <Button color="link-gray" size="sm" onClick={() => prepend(emptyAdditionalInsured())} data-autotest="uw-information-additional-insureds-add-row-button">
                    + Add Row
                </Button>
            }
        >
            <EditableCellProvider>
                <div className="relative overflow-hidden">
                <Table
                    aria-label="Additional Insured"
                    wrapperClassName="min-h-96"
                    className="[&_tbody_tr:last-child]:border-b-0!"
                >
                    <Table.Header>
                        <Table.Head id="entityName" label="Entity Name" isRowHeader className="w-108 pl-9" />
                        <Table.Head id="coverage" label="Coverage" />
                        <Table.Head id="retroDate" label="Retro Date" className="w-52 pl-9" />
                        <Table.Head id="actions" label="" className={`${STICKY_HEAD} w-20`} />
                    </Table.Header>
                    <Table.Body items={fields}>
                        {(row) => {
                            const idx = fields.findIndex((f) => f.id === row.id);
                            return (
                                <Table.Row id={row.id}>
                                    <Table.Cell className="w-108">
                                        <Controller
                                            control={control}
                                            name={`additionalInsureds.${idx}.entityName`}
                                            render={({ field, fieldState }) => (
                                                <EditableTextCell
                                                    ariaLabel="Entity Name"
                                                    placeholder="Type Entity Name"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    isInvalid={fieldState.invalid}
                                                    errorMessage={fieldState.error?.message}
                                                    data-autotest="uw-information-additional-insured-entity-name-input"
                                                />
                                            )}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-6">
                                            <Controller
                                                control={control}
                                                name={`additionalInsureds.${idx}.gl`}
                                                render={({ field }) => (
                                                    <Checkbox label="GL" isSelected={field.value} onChange={field.onChange} data-autotest="uw-information-additional-insured-gl-checkbox" />
                                                )}
                                            />
                                            <Controller
                                                control={control}
                                                name={`additionalInsureds.${idx}.pl`}
                                                render={({ field }) => (
                                                    <Checkbox label="PL" isSelected={field.value} onChange={field.onChange} data-autotest="uw-information-additional-insured-pl-checkbox" />
                                                )}
                                            />
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell className="w-52">
                                        <Controller
                                            control={control}
                                            name={`additionalInsureds.${idx}.retroactiveDate`}
                                            render={({ field, fieldState }) => (
                                                <EditableDateCell
                                                    ariaLabel="Retro Date"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    isInvalid={fieldState.invalid}
                                                    errorMessage={fieldState.error?.message}
                                                    data-autotest="uw-information-additional-insured-retro-date-input"
                                                />
                                            )}
                                        />
                                    </Table.Cell>
                                    <Table.Cell className={STICKY_CELL}>
                                        <div className="flex justify-end">
                                            <ButtonUtility
                                                color="tertiary"
                                                size="sm"
                                                icon={Trash02}
                                                tooltip="Delete row"
                                                onClick={() => remove(idx)}
                                                data-autotest="uw-information-additional-insured-delete-row-button"
                                                className="hover:bg-[var(--color-utility-neutral-100)]"
                                            />
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        }}
                    </Table.Body>
                </Table>
                {fields.length === 0 && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="pointer-events-auto">
                            <TableEmptyState
                                icon={FilePlus02}
                                title="Add Additional Insured"
                                description="Use the button below to start building the Additional Insureds list"
                                onAdd={() => prepend(emptyAdditionalInsured())}
                                data-autotest="uw-information-additional-insureds-empty-add-row-button"
                            />
                        </div>
                    </div>
                )}
                {/* Faux sticky-right boundary — only when populated. */}
                {fields.length > 0 && (
                    <div
                        aria-hidden="true"
                        className="bg-border-secondary pointer-events-none absolute top-0 bottom-0 z-10 hidden w-px md:block"
                        style={{ right: 80 }}
                    />
                )}
                </div>
            </EditableCellProvider>
        </FormGroup>
    );
}
